/**
 * Redeeming an invitation code: ONE copy, read by /start (src/pages/StartTrial.tsx), today's /pricing
 * (src/pages/Pricing.tsx) and the pricing page's rebuild in the new design. Until 2026-09-30 the two pages carried the
 * same ~250 lines each, differing only in where a signed-out visitor comes back to and in two sentences (and /pricing
 * alone dropped the cached billing answer after a redemption; both do now, since a redemption can mint a trial or a
 * credit on either page).
 *
 * The flow, as the owner directives of 2026-08-02 and 2026-08-09 set it:
 *  - a code in the address (`?code=`) pre-fills the field; the page decides what to do with it (`onPrefill`: /start
 *    focuses its button, /pricing scrolls to the section);
 *  - SIGNED OUT, a shared link auto-advances after 900 ms (a restoring session settles first): the READ-ONLY check
 *    decides between asking "do you already have an account?" (a new code) and signing in (a code already linked);
 *    opening a link can never consume a code;
 *  - the auto-redeem flag (`pending_trial_code` in sessionStorage) is set only when the person COMMITS to signing in or
 *    creating an account, so backing out leaves nothing behind; on the way back it is consumed at once, so a refresh
 *    never redeems twice;
 *  - SIGNED IN, a link only pre-fills: an explicit press redeems, so a forwarded link cannot spend a code on the wrong
 *    account;
 *  - the first paint after coming back from /auth already shows the focused shell (`authReturnBoot`), with an 8 s
 *    safety drop and a hand-off to the live redemption state.
 *
 * Every call goes through the shared client (src/services/accessCodeClient.ts), which never throws and maps every
 * server error to a message.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RedemptionOutcome } from '../components/redeem/redemptionWords';
import { checkAccessCode as checkAccessCodeRequest, redeemAccessCode as redeemAccessCodeRequest } from '../services/accessCodeClient';
import { invalidateBillingSnapshot } from '../services/billingSnapshotCache';
import { prefetchOnboardingRecommendations } from '../services/onboardingRecsPrefetch';

export const PENDING_CODE_KEY = 'pending_trial_code';

/** What an already-linked code says on the sign-in page (neutral: the pre-auth probe discloses no email, LP-R5). */
export const CODE_ALREADY_LINKED_HINT = 'This code is already linked to an account. Sign in to continue.';

export interface AccessCodeRedemptionOptions {
  isAuthenticated: boolean;
  /** The signed-in person's id: their cached billing answer is dropped when a redemption starts. */
  uid?: string | null;
  /** The page a visitor comes back to after signing in (`/start` or `/pricing`); the code rides it as `?code=`. */
  returnPath: string;
  /** The page's own words for a new account's success, a returning one's, and (optionally) a workstation being made. */
  words: { accepted: string; welcomeBack: string; provisioning?: string };
  /** What the page does once a code arrives in the address (after the field is filled). */
  onPrefill?: () => void;
}

const readPending = (): string | null => {
  try { return sessionStorage.getItem(PENDING_CODE_KEY); } catch { return null; }
};
const setPending = (code: string) => {
  try { sessionStorage.setItem(PENDING_CODE_KEY, code); } catch { /* storage off: the person enters the code again */ }
};
const clearPending = () => {
  try { sessionStorage.removeItem(PENDING_CODE_KEY); } catch { /* nothing to clear */ }
};

export function useAccessCodeRedemption(options: AccessCodeRedemptionOptions) {
  const { isAuthenticated, uid, returnPath, words, onPrefill } = options;
  const navigate = useNavigate();
  const location = useLocation();

  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSuccess, setCodeSuccess] = useState(false);
  const [codeSuccessMessage, setCodeSuccessMessage] = useState(words.accepted);
  const [prefilledFromUrl, setPrefilledFromUrl] = useState(false);
  const [returnedFromAuth, setReturnedFromAuth] = useState(false);
  // Signed-out + valid unused code → ask whether they already have an account
  const [accountQuestionCode, setAccountQuestionCode] = useState<string | null>(null);
  // Signed-in wallet-credit / trial-extension confirmations (no onboarding)
  const [redeemOutcome, setRedeemOutcome] = useState<RedemptionOutcome | null>(null);

  const onPrefillRef = useRef(onPrefill);
  onPrefillRef.current = onPrefill;
  const wordsRef = useRef(words);
  wordsRef.current = words;

  // Pre-fill the code from the address.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefilledCode = params.get('code');
    if (prefilledCode) {
      setCodeInput(prefilledCode.toUpperCase());
      setPrefilledFromUrl(true);
      setTimeout(() => onPrefillRef.current?.(), 350);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('code') && isAuthenticated) setReturnedFromAuth(true);
  }, [isAuthenticated, location.search]);

  // The first paint after coming back from /auth: returnedFromAuth and the auto-redeem's codeLoading are set in
  // post-commit effects, so without this the full page flashed before the focused shell. It starts from the same
  // signals the auto-redeem consumes (?code= in the address + the pending code), drops after 8 s if auth never
  // settles, and hands off to the live redemption state the moment it starts (or it would mask a wallet-credit or
  // trial-extension outcome behind a stuck "applying…" card).
  const [authReturnBoot, setAuthReturnBoot] = useState(() => {
    try {
      const hasCode = new URLSearchParams(window.location.search).has('code');
      return hasCode && Boolean(sessionStorage.getItem(PENDING_CODE_KEY));
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!authReturnBoot) return undefined;
    const t = setTimeout(() => {
      if (!isAuthenticated) setAuthReturnBoot(false);
    }, 8000);
    return () => clearTimeout(t);
  }, [authReturnBoot, isAuthenticated]);
  useEffect(() => {
    if (codeLoading && authReturnBoot) setAuthReturnBoot(false);
  }, [codeLoading, authReturnBoot]);

  // ── Redeem (called once the person is signed in) ──
  const redeemCode = useCallback(async (code: string) => {
    setCodeLoading(true);
    setCodeError(null);
    // A redemption can mint a trial subscription or credit: either way the cached billing answer is out of date.
    invalidateBillingSnapshot(uid);
    try {
      const result = await redeemAccessCodeRequest(code);
      if (!result.success) {
        setCodeError(result.message);
        return;
      }
      // Route by redemption mode (see functions/src/accessCodeRedemption.ts)
      if (result.mode === 'wallet_credit') {
        setRedeemOutcome({ mode: 'wallet_credit', creditCents: result.creditAmountCents ?? 0 });
        return;
      }
      if (result.mode === 'trial_extension') {
        setRedeemOutcome({
          mode: 'trial_extension',
          creditCents: result.creditAddedCents ?? 0,
          expiresAt: result.expiresAt ?? null,
        });
        return;
      }
      const w = wordsRef.current;
      setCodeSuccessMessage(result.returning ? w.welcomeBack : w.accepted);
      setCodeSuccess(true);
      // Start the firm-aware recommendation generation NOW, in parallel with the redirect and the onboarding
      // bootstrap: the profile page's fetch then hits a warm server cache (2026-08-04 owner feedback).
      prefetchOnboardingRecommendations();
      if (result.returning) {
        setTimeout(() => navigate('/desktop'), 1200);
      } else if (result.vmProvisioned && w.provisioning) {
        setCodeSuccessMessage(w.provisioning);
        // Longer, so the message is read before onboarding.
        setTimeout(() => navigate('/onboarding'), 3000);
      } else {
        setTimeout(() => navigate('/onboarding'), 1200);
      }
    } finally {
      setCodeLoading(false);
    }
  }, [navigate, uid]);

  // Auto-redeem: once, when the person comes back from /auth with a pending code. Cleared at once so a refresh never
  // redeems twice.
  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingCode = readPending();
    if (pendingCode) clearPending();
    if (!pendingCode) return;
    const code = pendingCode.toUpperCase();
    setCodeInput(code);
    void redeemCode(code);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signed out: check the code (READ-ONLY) and send the person to the right step.
  const routeSignedOutCode = useCallback(async (trimmedCode: string) => {
    setCodeLoading(true);
    setCodeError(null);
    try {
      const check = await checkAccessCodeRequest(trimmedCode);
      if (!check.success) {
        setCodeError(check.message);
        return;
      }
      if (check.status === 'unused') {
        // A new code → ask whether they already have an account, so it applies to the RIGHT one. The auto-redeem flag
        // is set when they commit (answerAccountQuestion), never here.
        setAccountQuestionCode(trimmedCode);
      } else {
        // Already linked → sign back in. Navigating now is committing.
        setPending(trimmedCode);
        navigate('/auth', {
          state: {
            returnUrl: `${returnPath}?code=${encodeURIComponent(trimmedCode)}`,
            authMode: 'login',
            loginHint: CODE_ALREADY_LINKED_HINT,
          },
        });
      }
    } finally {
      setCodeLoading(false);
    }
  }, [navigate, returnPath]);

  // The button (and Enter in the field).
  const handleRedeemCode = useCallback(async () => {
    const trimmedCode = codeInput.trim().toUpperCase();
    if (!trimmedCode) {
      setCodeError('Please enter an access code.');
      return;
    }
    if (isAuthenticated) {
      await redeemCode(trimmedCode);
      return;
    }
    await routeSignedOutCode(trimmedCode);
  }, [codeInput, isAuthenticated, redeemCode, routeSignedOutCode]);

  const handleCodeKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') void handleRedeemCode();
  }, [handleRedeemCode]);

  /** The field: typing upper-cases and clears the last error. */
  const typeCode = useCallback((raw: string) => {
    setCodeInput(raw.toUpperCase());
    setCodeError(null);
  }, []);

  // One-click invite links: a signed-out visitor auto-advances into the account question. A signed-in one keeps the
  // pre-fill and an explicit press. An auth return in flight belongs to the auto-redeem above.
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkCode = (params.get('code') || '').trim().toUpperCase();
    if (!linkCode || autoAdvancedRef.current || isAuthenticated) return undefined;
    if (readPending()) return undefined;
    const t = setTimeout(() => {
      autoAdvancedRef.current = true;
      void routeSignedOutCode(linkCode);
    }, 900);
    return () => clearTimeout(t);
  }, [location.search, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The account question, answered: they commit, so the auto-redeem flag is set now. */
  const answerAccountQuestion = useCallback((authMode: 'login' | 'signup') => {
    if (!accountQuestionCode) return;
    setPending(accountQuestionCode);
    navigate('/auth', {
      state: {
        returnUrl: `${returnPath}?code=${encodeURIComponent(accountQuestionCode)}`,
        authMode,
      },
    });
  }, [accountQuestionCode, navigate, returnPath]);

  /** Back out of the account question: nothing pending is left behind. */
  const leaveAccountQuestion = useCallback(() => {
    clearPending();
    setAccountQuestionCode(null);
  }, []);

  /** Sign in with nothing redeemed; the person comes back to this page. */
  const handleLogin = useCallback(() => {
    navigate('/auth', { state: { returnUrl: returnPath } });
  }, [navigate, returnPath]);

  /** The focused progress shell shows while a signed-in redemption runs or finishes, and on the first paint back. */
  const progressShown = ((codeLoading || codeSuccess) && isAuthenticated)
    || (authReturnBoot && !codeError && !codeSuccess && !codeLoading);

  return {
    codeInput,
    typeCode,
    codeLoading,
    codeError,
    codeSuccess,
    codeSuccessMessage,
    prefilledFromUrl,
    returnedFromAuth,
    accountQuestionCode,
    redeemOutcome,
    authReturnBoot,
    progressShown,
    handleRedeemCode,
    handleCodeKeyDown,
    answerAccountQuestion,
    leaveAccountQuestion,
    handleLogin,
  };
}
